import { S3Client, PutObjectCommand, GetObjectCommand, HeadObjectCommand } from '@aws-sdk/client-s3'
import { SSMClient, GetParameterCommand } from '@aws-sdk/client-ssm'
import * as _ from 'lodash'
import { compile } from 'handlebars'
import { CBUtils, FILE_TYPES, getJWTPayload } from '@landscape/ui-lib'
import { normalizeHeaders, readyHeaders } from './fileValidation'
import { saveAs } from 'file-saver'
import { setModalOptions } from '@landscape/ui-redux'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'
import { Upload } from '@aws-sdk/lib-storage'

const { VITE_REACT_APP_INPUT_BUCKET, VITE_REACT_APP_RESULT_BUCKET, VITE_REACT_APP_DOCS_BUCKET } =
  import.meta.env
const DOMAIN_NAME = 'tecd'
const cb = window.cb

const getDisplayName = () => {
  const { basicProfile } = window.cb.core.iam.getAuthSession()

  if (basicProfile?.firstName && basicProfile?.lastName) {
    return `${basicProfile.firstName} ${basicProfile.lastName}`
  }

  return ''
}

/**
 *
 * @param {*} origFileName
 * @param {*} fileInfo Contains meta info like name, size, type
 */
const uploadFileToS3 = async (
  origFileName: string,
  file: any,
  setPercentage: any,
  dispatch: any,
  oid: string
) => {
  const awsCredentials = cb.core.iam.getTempAWSCreds(DOMAIN_NAME)
  const payload = getJWTPayload()

  // check if the credentials are there and have not expired

  if (!awsCredentials || !payload) {
    return new Promise((resolve, reject) => {
      reject('Unable to authenticate. Please log out, log back in, and upload the file again.')
    })
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async () => {
      const prefix = cb.core.iam.getTempAWSCredsSubject() + '/'
      // const matchingRoleOrg = getRoleOrgList()
      // const oid = matchingRoleOrg.oid
      const origFileNameWithCSVExt = origFileName.replace(/\.[^/.]+$/, '.csv')
      const fileName = `${oid}-${Date.now()}-${origFileNameWithCSVExt}`
      const origFileContent: any = reader.result
      const { invalidHeaders, headersToCheck, uniqueColumnCount } = readyHeaders(origFileContent)

      if (!_.isEmpty(invalidHeaders) || headersToCheck.length !== 40 || uniqueColumnCount !== 40) {
        resolve([
          {
            kind: 'fatal',
            message:
              'File headers are incorrectly named or missing. Please make sure your headers match those from the Landscape File Template on the Help Page.',
          },
        ])
        return
      }

      const { validate_csv } = await import('landscape-fast-csv')
      let wasmOutput = validate_csv(normalizeHeaders(origFileContent))
      let { records_count: recordsCount, errs_or_warns: validationErrors } = wasmOutput || []

      validationErrors = validationErrors.map(({ line_nums, message, kind }) => {
        const template = compile(message)
        // We only want the message to display 5 instances of each error or warn message
        const isOverFiveLineNums = line_nums.length > 5
        const firstFiveLineNums = line_nums.slice(0, 5)
        return {
          kind,
          message: `${template({ csvLineNums: firstFiveLineNums })}${
            isOverFiveLineNums ? '...' : ''
          }`,
        }
      })

      if (!_.find(validationErrors, _.matchesProperty('kind', 'fatal'))) {
        const landscapeRoles = payload.cb.ro.filter((r) => r.rid === '809' || r.rid === '810')

        const payloadWithDisplayName: any = {
          jwtPayload: {
            ...payload,
            cb: {
              ...payload.cb,
              ro: landscapeRoles,
            },
          },
          displayName: getDisplayName(),
        }

        const connectionId = sessionStorage.getItem('connectionId')
        if (connectionId) {
          payloadWithDisplayName.connectionId = connectionId
        }

        let payLoadStr = JSON.stringify(payloadWithDisplayName)

        // console.log('Metadata', {
        //   uploaderinfo: payLoadStr,
        //   recordsCount: recordsCount.toString(),
        // })

        const params = {
          Bucket: VITE_REACT_APP_INPUT_BUCKET!,
          Key: prefix + fileName,
          Body: origFileContent,
          Metadata: {
            uploaderinfo: payLoadStr,
            recordsCount: recordsCount.toString(),
          },
        }

        const uploadToS3 = async () => {
          const s3Client = new S3Client({ region: 'us-east-1', credentials: awsCredentials })
          const upload = new Upload({
            client: s3Client,
            params,
            leavePartsOnError: false,
          })

          upload.on('httpUploadProgress', (progress) => {
            if (progress.total && progress.loaded !== undefined) {
              const percentage = Math.round((progress.loaded / progress.total) * 100)
              setPercentage(percentage)
            }
          })

          return upload
            .done()
            .then((data) => {
              resolve([])
              // console.log('uploaded data', JSON.stringify(data, null, 2))
              return []
            })
            .catch((uploadError) => {
              console.error(uploadError)
              throw new Error('Network Error. Please try to upload the file again.')
            })
        }

        if (_.isEmpty(validationErrors)) {
          uploadToS3()
        } else {
          // Some warning messages are present
          const displayErrors: string[] = []
          let warningMessageIndex = 0

          // Only warning errors
          displayErrors.push(
            `Although your file will be processed, the following items may affect your Landscape output file and applicant views. We recommend you correct and re-submit if possible. \n \n`
          )

          validationErrors.forEach((error: any) => {
            ++warningMessageIndex
            displayErrors.push(`Warning ${warningMessageIndex}: ${error.message}\n`)
          })

          dispatch(
            setModalOptions({
              showModal: true,
              body: displayErrors,
              status: 'warning',
              title: 'Record Level Warnings',
              primaryLabel: 'OK',
              cancelLabel: 'Cancel Upload',
              confirm: true,
              handlerFunction: uploadToS3,
              contactInfo: true,
            })
          )
        }
      } else {
        // There are critical fatal errors
        resolve(validationErrors)
      }
    } // end of reader onload
    reader.readAsText(file, 'UTF-8')
  })
}

export interface FileMetadataType {
  type: FILE_TYPES
  originalFileName: string
  di_code: string
  jobId: string
  clientType?: string
}

const getS3Metadata = (metadata: FileMetadataType) => {
  const bucket = getBucket(metadata.type)
  const { key, outputFileName } = getKey(metadata)

  return {
    bucket,
    key,
    outputFileName,
  }
}

const getBucket = (type: FILE_TYPES) => {
  let bucketType
  switch (type) {
    case FILE_TYPES.DOC:
      bucketType = VITE_REACT_APP_DOCS_BUCKET
      break
    // As of now, AGG/JOB/QC files all exist in the VITE_REACT_APP_RESULT_BUCKET
    default:
      bucketType = VITE_REACT_APP_RESULT_BUCKET
      break
  }
  return bucketType
}

const getKey = (metadata: FileMetadataType) => {
  const { type, originalFileName, di_code, jobId, clientType } = metadata
  let key
  let outputFileName
  switch (type) {
    case FILE_TYPES.AGG:
      outputFileName = `${di_code}_full.csv`
      key = `external/agg/${di_code}_full.part_00000`
      break
    case FILE_TYPES.JOB:
      outputFileName = `${di_code}_${jobId}_${originalFileName}.csv`
      key = `external/job/${di_code}_${jobId}_${originalFileName}.part_00000`
      break
    case FILE_TYPES.QC:
      outputFileName = `${originalFileName}_qc_check_${clientType}.csv`
      key = `${clientType}/qc/${di_code}_${jobId}_${originalFileName}`
      break
    default:
      outputFileName = originalFileName
      key = originalFileName
      break
  }
  return {
    key,
    outputFileName,
  }
}

interface downloadAnyFileFromS3Props {
  metadata: FileMetadataType
  setLoading?: Function
  onFileDoesNotExist?: Function
  callback?: Function
}

const downloadAnyFileFromS3 = async (props: downloadAnyFileFromS3Props) => {
  const { metadata, setLoading, onFileDoesNotExist, callback } = props
  const { bucket, key, outputFileName } = getS3Metadata(metadata)

  const jwtPayload = getJWTPayload()
  const awsCredentials = cb.core.iam.getTempAWSCreds(DOMAIN_NAME)

  // Check if the credentials are there and have not expired
  if (awsCredentials && jwtPayload) {
    // Get a signed url to the file in S3. Expire in 10 seconds.
    const params = {
      Bucket: bucket,
      Key: key,
    }
    const s3Client = new S3Client({ region: 'us-east-1', credentials: awsCredentials })
    const command = new GetObjectCommand(params)
    const fileUrl = await getSignedUrl(s3Client, command, { expiresIn: 60 })
    setLoading && setLoading(true)
    return fetch(fileUrl)
      .then((res) => {
        if (res.status === 403 && onFileDoesNotExist) {
          onFileDoesNotExist && onFileDoesNotExist()
          setLoading && setTimeout(() => setLoading(false), 1000)
        } else {
          return res.blob()
        }
      })
      .then((blob) => {
        saveAs(blob, outputFileName)
        setLoading && setLoading(false)
      })
      .then(() => {
        callback && callback()
      })
  }
}

const getS3ObjMetadata = async ({
  s3ObjectKey,
  bucket = VITE_REACT_APP_RESULT_BUCKET,
}: {
  s3ObjectKey: string
  bucket?: string
}) => {
  const cbUtils = new CBUtils('tecd')
  const jwtPayload = cbUtils.getJwtPayload()
  const awsCredentials = cbUtils.getTempAWSCredentials()
  if (jwtPayload && awsCredentials) {
    const params = {
      Bucket: bucket,
      Key: s3ObjectKey,
    }
    try {
      const s3Client = new S3Client({ region: 'us-east-1', credentials: awsCredentials })
      const command = new HeadObjectCommand(params)
      const objMetadata = await s3Client.send(command)
      return objMetadata
    } catch (error: any) {
      console.error(error)
      throw error
    }
  }
}

const getParameterFromSSM = async (parameterName: string) => {
  const cbUtils = new CBUtils('tecd')
  const jwtPayload = cbUtils.getJwtPayload()
  const awsCredentials = cbUtils.getTempAWSCredentials()

  if (jwtPayload && awsCredentials) {
    const ssmClient = new SSMClient({
      region: 'us-east-1',
      credentials: awsCredentials,
    })

    const command = new GetParameterCommand({ Name: parameterName })
    const response = await ssmClient.send(command)
    return response.Parameter?.Value
  }

  return ''
}

export { uploadFileToS3, getS3ObjMetadata, downloadAnyFileFromS3, getParameterFromSSM }
